import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Building2, Plus, Pencil, Users, Loader2, X, Check,
  AlertCircle, CheckCircle2, Clock, ChevronDown, ChevronUp,
  UserPlus, Trash2, Ban, Shield, Key, Copy, Landmark, FlaskConical, FileStack
} from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import { EmpresaParceira, Profile } from '../../types';
import ConfirmationModal from '../../src/components/ConfirmationModal';
import { gerarBoletoGran } from '../../src/lib/gatewayResolver';
import LoadingSpinner from '../../src/components/LoadingSpinner';

// ── Helpers ──────────────────────────────────────────────────

const STATUS_CONFIG = {
  ativo:    { label: 'Ativo',    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  inativo:  { label: 'Inativo',  color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' },
  pendente: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
};

const BANCO_LABEL: Record<string, string> = {
  inter:   'Banco Inter',
  sicoob:  'Sicoob',
  manual:  'Manual',
  asaas:   'Asaas',
};

const MODULOS_DISPONIVEIS = [
  { key: 'financeiro', label: 'Financeiro' },
  { key: 'ucs',        label: 'UCs / Clientes' },
];

const ROLE_LABEL: Record<string, string> = {
  empresa_parceira_admin:    'Administrador',
  empresa_parceira_operador: 'Operador',
};

// ── Tipos internos ───────────────────────────────────────────

interface EmpresaForm {
  nome: string;
  cnpj: string;
  status: 'ativo' | 'inativo' | 'pendente';
  banco_conta: 'inter' | 'sicoob' | 'manual' | 'asaas';
  modulos_habilitados: string[];
  boleto_emissor_nome: string;
  boleto_emissor_cnpj: string;
  boleto_emissor_email: string;
  boleto_emissor_telefone: string;
}

const emptyForm = (): EmpresaForm => ({
  nome: '',
  cnpj: '',
  status: 'pendente',
  banco_conta: 'inter',
  modulos_habilitados: ['financeiro', 'ucs'],
  boleto_emissor_nome: '',
  boleto_emissor_cnpj: '',
  boleto_emissor_email: '',
  boleto_emissor_telefone: '',
});

// ── Modal de criação/edição ──────────────────────────────────

const EmpresaModal = ({
  empresa,
  onClose,
  onSaved,
}: {
  empresa: EmpresaParceira | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<EmpresaForm>(
    empresa
      ? {
          nome: empresa.nome,
          cnpj: empresa.cnpj || '',
          status: empresa.status,
          banco_conta: empresa.banco_conta,
          modulos_habilitados: empresa.modulos_habilitados,
          boleto_emissor_nome: empresa.boleto_emissor_nome || '',
          boleto_emissor_cnpj: empresa.boleto_emissor_cnpj || '',
          boleto_emissor_email: empresa.boleto_emissor_email || '',
          boleto_emissor_telefone: empresa.boleto_emissor_telefone || '',
        }
      : emptyForm()
  );

  const toggleModulo = (key: string) =>
    setForm(f => ({
      ...f,
      modulos_habilitados: f.modulos_habilitados.includes(key)
        ? f.modulos_habilitados.filter(m => m !== key)
        : [...f.modulos_habilitados, key],
    }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.modulos_habilitados.length === 0) { addToast('Selecione pelo menos um módulo.', 'error'); return; }

    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        cnpj: form.cnpj.trim() || null,
        status: form.status,
        banco_conta: form.banco_conta,
        modulos_habilitados: form.modulos_habilitados,
        // Vazio grava NULL, e NULL é o que faz o boleto cair no titular da UC
        // (comportamento anterior). String vazia apagaria o bloco no PDF.
        boleto_emissor_nome: form.boleto_emissor_nome.trim() || null,
        boleto_emissor_cnpj: form.boleto_emissor_cnpj.replace(/\D/g, '') || null,
        boleto_emissor_email: form.boleto_emissor_email.trim() || null,
        boleto_emissor_telefone: form.boleto_emissor_telefone.trim() || null,
      };

      if (empresa) {
        const { error } = await supabase.from('empresas_parceiras').update(payload).eq('id', empresa.id);
        if (error) throw error;
        addToast('Empresa parceira atualizada.', 'success');
        logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'empresas_parceiras', acao: 'atualizar_empresa_parceira', origem: 'acao' });
      } else {
        const { error } = await supabase.from('empresas_parceiras').insert({ ...payload, created_by: profile?.id });
        if (error) throw error;
        addToast('Empresa parceira criada.', 'success');
        logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'empresas_parceiras', acao: 'criar_empresa_parceira', origem: 'acao' });
      }

      onSaved();
      onClose();
    } catch (err: any) {
      addToast('Erro ao salvar: ' + (err.message || 'Erro desconhecido'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
          <h3 className="font-bold text-lg text-gray-800 dark:text-white flex items-center gap-2">
            <Building2 size={20} className="text-primary" />
            {empresa ? 'Editar Empresa Parceira' : 'Nova Empresa Parceira'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-4 max-h-[calc(100vh-140px)] overflow-y-auto">
          {/* Nome */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome da Empresa <span className="text-red-500">*</span></label>
            <input
              required
              type="text"
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              placeholder="Ex: Gran"
              value={form.nome}
              onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
            />
          </div>

          {/* CNPJ */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">CNPJ</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              placeholder="00.000.000/0001-00"
              value={form.cnpj}
              onChange={e => setForm(f => ({ ...f, cnpj: e.target.value }))}
            />
          </div>

          {/* Status */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
            <select
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value as EmpresaForm['status'] }))}
            >
              <option value="pendente">Pendente</option>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
          </div>

          {/* Divisão do boleto: por UC desde 26/09/2026 */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Divisão do Boleto</label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Definida em cada UC (% da Vigor na tarifa cheia), pelo Financeiro ou adm, no cadastro da UC.
            </p>
          </div>

          {/* Banco */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Banco para Emissão <span className="text-red-500">*</span></label>
            <select
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              value={form.banco_conta}
              onChange={e => setForm(f => ({ ...f, banco_conta: e.target.value as EmpresaForm['banco_conta'] }))}
            >
              <option value="inter">Banco Inter</option>
              <option value="sicoob">Sicoob</option>
              <option value="manual">Manual</option>
              <option value="asaas">Asaas</option>
            </select>
          </div>

          {/* Módulos */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Módulos Habilitados <span className="text-red-500">*</span></label>
            <div className="flex flex-wrap gap-2">
              {MODULOS_DISPONIVEIS.map(m => {
                const on = form.modulos_habilitados.includes(m.key);
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => toggleModulo(m.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                      on
                        ? 'bg-primary/10 text-primary border-primary/30 dark:bg-primary/20'
                        : 'bg-gray-50 text-gray-500 border-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {on && <Check size={12} />}
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Emissor do boleto — quem COBRA, que não é necessariamente o cadastro acima */}
          <div className="space-y-3 rounded-xl border border-amber-100 dark:border-amber-900/30 bg-amber-50/50 dark:bg-amber-900/10 p-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Emissor do boleto</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                É o que aparece no topo do boleto para o cliente. Use a razão social e o CNPJ da
                empresa <strong>titular da conta</strong> em que a cobrança é gerada — pode ser
                diferente do cadastro acima. Em branco, o boleto usa o titular da UC.
              </p>
            </div>
            <input
              type="text"
              value={form.boleto_emissor_nome}
              onChange={e => setForm(f => ({ ...f, boleto_emissor_nome: e.target.value }))}
              placeholder="Razão social (ex.: ZGRAN GESTAO ENERGETICA E TECNOLOGIA LTDA)"
              className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                value={form.boleto_emissor_cnpj}
                onChange={e => setForm(f => ({ ...f, boleto_emissor_cnpj: e.target.value }))}
                placeholder="CNPJ do emissor"
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <input
                type="text"
                value={form.boleto_emissor_telefone}
                onChange={e => setForm(f => ({ ...f, boleto_emissor_telefone: e.target.value }))}
                placeholder="Telefone de atendimento"
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <input
              type="email"
              value={form.boleto_emissor_email}
              onChange={e => setForm(f => ({ ...f, boleto_emissor_email: e.target.value }))}
              placeholder="E-mail de atendimento"
              className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          <div className="pt-2 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-primary/20 disabled:opacity-50"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
              {empresa ? 'Salvar Alterações' : 'Criar Empresa'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

// ── Modal de criação de usuário da empresa parceira ──────────

const AddUserModal = ({
  empresa,
  onClose,
  onSaved,
}: {
  empresa: EmpresaParceira;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    role: 'empresa_parceira_admin' as 'empresa_parceira_admin' | 'empresa_parceira_operador',
    jobTitle: '',
    whatsapp: '',
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          ...form,
          departamento: 'Financeiro',
          empresa_parceira_id: empresa.id,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      addToast(`Usuário criado e vinculado à ${empresa.nome}.`, 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'empresas_parceiras', acao: 'criar_usuario_empresa_parceira', origem: 'acao' });
      onSaved();
      onClose();
    } catch (err: any) {
      addToast('Erro ao criar usuário: ' + (err.message || 'Erro desconhecido'), 'error', 7000);
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
          <div>
            <h3 className="font-bold text-lg text-gray-800 dark:text-white">Novo Usuário — {empresa.nome}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Este usuário terá acesso apenas aos dados da {empresa.nome}.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome Completo <span className="text-red-500">*</span></label>
            <input
              required type="text"
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              placeholder="Ex: João da Silva"
              value={form.fullName}
              onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Email <span className="text-red-500">*</span></label>
            <input
              required type="email"
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              placeholder="joao@gran.com.br"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Cargo / Função</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              placeholder="Ex: Gerente Financeiro"
              value={form.jobTitle}
              onChange={e => setForm(f => ({ ...f, jobTitle: e.target.value }))}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Permissão</label>
            <select
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              value={form.role}
              onChange={e => setForm(f => ({ ...f, role: e.target.value as typeof form.role }))}
            >
              <option value="empresa_parceira_admin">Administrador — cria e edita UCs, faturas e usuários</option>
              <option value="empresa_parceira_operador">Operador — acesso somente leitura e emissão de boletos</option>
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Senha Inicial <span className="text-red-500">*</span></label>
            <input
              required type="text"
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              placeholder="Mínimo 6 caracteres"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            />
          </div>

          <div className="pt-2 flex gap-3">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-primary/20 disabled:opacity-50">
              {saving ? <Loader2 size={18} className="animate-spin" /> : <UserPlus size={18} />}
              Criar Usuário
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

// ── Modal de credenciais Asaas (empresa parceira) ────────────────
//
// Desde 06/08/2026 é a empresa parceira que EMITE o boleto único, na conta Asaas
// dela; a parte da Vigor sai por split para a carteira da Vigor (Config > Conta
// Asaas Vigor). Ou seja: a chave de API aqui é a que de fato cobra — sem ela a
// emissão é recusada —, e os eventos PAYMENT_* passam a vir do webhook desta conta.
//
// Produção e sandbox são contas Asaas SEPARADAS e coexistem: cada aba edita a sua
// própria linha (empresa + banco + ambiente). Mesmo desenho de VigorGatewayAsaasView.

type AmbienteAsaas = 'producao' | 'sandbox';

interface StatusAsaasAmbiente {
  configurado: boolean;
  webhook_configurado: boolean;
  ambiente: string;
  wallet_id_proprio: string | null;
}

const AsaasCredenciaisModal = ({
  empresa,
  onClose,
}: {
  empresa: EmpresaParceira;
  onClose: () => void;
}) => {
  const { addToast } = useToast();
  const { profile } = useAuth();
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [ambientes, setAmbientes] = useState<Record<AmbienteAsaas, StatusAsaasAmbiente> | null>(null);
  const [aba, setAba] = useState<AmbienteAsaas>('producao');
  const [apiKey, setApiKey] = useState('');
  const [walletIdProprio, setWalletIdProprio] = useState('');
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [novoToken, setNovoToken] = useState<string | null>(null);

  const webhookUrl = `${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/asaas-webhook`;
  const status = ambientes?.[aba] ?? null;
  const isSandbox = aba === 'sandbox';

  const loadStatus = async () => {
    setLoadingStatus(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-empresa-parceira-banco', {
        body: { action: 'getStatus', empresa_parceira_id: empresa.id, banco: 'asaas' },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setAmbientes(data.ambientes);
    } catch {
      addToast('Erro ao carregar as credenciais Asaas.', 'error');
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  // Troca de aba = outro conjunto de credenciais: limpa o que estava digitado para
  // não salvar a chave de produção dentro do sandbox por descuido. Depende SÓ de
  // `aba`, de propósito — com `ambientes` na lista, todo loadStatus() apagaria da
  // tela o token recém-gerado, que só é exibível uma vez.
  useEffect(() => {
    setApiKey('');
    setNovoToken(null);
  }, [aba]);

  // O walletId exibido acompanha o que está salvo no ambiente selecionado.
  useEffect(() => {
    setWalletIdProprio(ambientes?.[aba]?.wallet_id_proprio || '');
  }, [aba, ambientes]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const chave = apiKey.trim();
    const carteira = walletIdProprio.trim();

    if (!chave && !status?.configurado) {
      addToast('Informe a chave de API do Asaas desta empresa — é ela que emite o boleto.', 'error');
      return;
    }
    if (!chave && !carteira) {
      addToast('Nada para salvar.', 'error');
      return;
    }

    // Sem chave nova digitada, só a carteira é atualizada: a chave salva não é
    // recuperável para reexibir, e obrigar a redigitá-la só para corrigir o
    // walletId seria um convite a colar a chave errada.
    const body = chave
      ? {
        action: 'setCredenciais',
        empresa_parceira_id: empresa.id,
        banco: 'asaas',
        api_key: chave,
        wallet_id_proprio: carteira || null,
        ambiente: aba,
      }
      : {
        action: 'setWalletIdProprio',
        empresa_parceira_id: empresa.id,
        wallet_id_proprio: carteira,
        ambiente: aba,
      };

    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-empresa-parceira-banco', { body });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      addToast(`Credenciais de ${isSandbox ? 'sandbox' : 'produção'} salvas com sucesso.`, 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'empresas_parceiras', acao: 'salvar_credenciais_asaas_parceira', origem: 'acao' });
      setApiKey('');
      loadStatus();
    } catch (err: any) {
      addToast('Erro ao salvar credenciais: ' + (err.message || 'Erro desconhecido'), 'error', 7000);
    } finally {
      setSaving(false);
    }
  };

  const handleRotateToken = async () => {
    setRotating(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-empresa-parceira-banco', {
        body: { action: 'rotateWebhookToken', empresa_parceira_id: empresa.id, banco: 'asaas', ambiente: aba },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setNovoToken(data.token);
      logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'empresas_parceiras', acao: 'rotacionar_token_webhook_parceira', origem: 'acao' });
      loadStatus();
    } catch (err: any) {
      addToast('Erro ao gerar token: ' + (err.message || 'Erro desconhecido'), 'error', 7000);
    } finally {
      setRotating(false);
    }
  };

  const copyToClipboard = (value: string, label: string) => {
    navigator.clipboard.writeText(value);
    addToast(`${label} copiado.`, 'success');
  };

  const abaClasse = (alvo: AmbienteAsaas) => {
    const ativo = aba === alvo;
    if (!ativo) return 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border-transparent';
    return alvo === 'sandbox'
      ? 'text-amber-600 dark:text-amber-400 border-amber-500'
      : 'text-primary border-primary';
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden my-8">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
          <div>
            <h3 className="font-bold text-lg text-gray-800 dark:text-white">Credenciais Asaas — {empresa.nome}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              O boleto único é emitido nesta conta e o dinheiro do consumidor entra aqui; a parte da Vigor sai por
              split para a carteira cadastrada em Config &gt; Conta Asaas Vigor. A {empresa.nome} fica com o residual.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        {loadingStatus ? (
          <div className="flex justify-center py-10"><LoadingSpinner /></div>
        ) : (
          <>
            {/* Cada aba edita uma conta Asaas diferente — as duas convivem. */}
            <div className="flex border-b border-gray-100 dark:border-gray-700 px-2">
              <button
                onClick={() => setAba('producao')}
                className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-1.5 ${abaClasse('producao')}`}
              >
                <Landmark size={15} /> Produção
                {ambientes?.producao.configurado ? <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> : null}
              </button>
              <button
                onClick={() => setAba('sandbox')}
                className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-1.5 ${abaClasse('sandbox')}`}
              >
                <FlaskConical size={15} /> Sandbox
                {ambientes?.sandbox.configurado ? <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> : null}
              </button>
            </div>

            <div className="p-6 space-y-5">
              {isSandbox ? (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    <strong>Ambiente de teste.</strong> Conta Asaas separada, sem dinheiro real: as cobranças ficam
                    marcadas como sandbox e não entram em somatórios, inadimplência ou exportações. A chave começa
                    com <code>$aact_hmlg_</code>.
                  </p>
                </div>
              ) : null}

              <div className="flex items-center gap-4 text-sm">
                <span className={`flex items-center gap-1.5 ${status?.configurado ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                  <CheckCircle2 size={14} /> Chave de API {status?.configurado ? 'configurada' : 'não configurada'}
                </span>
                <span className={`flex items-center gap-1.5 ${status?.webhook_configurado ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                  <CheckCircle2 size={14} /> Webhook {status?.webhook_configurado ? 'configurado' : 'não configurado'}
                </span>
              </div>

              <form onSubmit={handleSave} className="space-y-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Chave de API (access_token) da {empresa.nome}
                    {status?.configurado ? null : <span className="text-red-500"> *</span>}
                  </label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                    placeholder={status?.configurado ? '•••••••• (preencha para substituir)' : (isSandbox ? '$aact_hmlg_...' : '$aact_prod_...')}
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                  />
                  <p className="text-[11px] text-gray-400">É com esta chave que a cobrança é criada. Sem ela, a emissão é recusada.</p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">WalletId da própria {empresa.nome} (referência)</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                    value={walletIdProprio}
                    onChange={e => setWalletIdProprio(e.target.value)}
                  />
                </div>
                <button type="submit" disabled={saving}
                  className={`w-full py-2.5 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 ${isSandbox ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/20' : 'bg-primary hover:bg-primary/90 shadow-primary/20'}`}>
                  {saving ? <Loader2 size={18} className="animate-spin" /> : <Key size={18} />}
                  Salvar credenciais de {isSandbox ? 'sandbox' : 'produção'}
                </button>
              </form>

              <div className="border-t border-gray-100 dark:border-gray-700 pt-4 space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Webhook de cobranças</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Cadastre esta URL no painel do Asaas da {empresa.nome} (Webhooks de Cobranças, eventos PAYMENT_*)
                    com o token gerado abaixo — é por aqui que a baixa do boleto chega no VigorHub. A URL é a mesma nos
                    dois ambientes: quem diz de onde veio o evento é o token.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input readOnly value={webhookUrl}
                    className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded-lg text-xs" />
                  <button type="button" onClick={() => copyToClipboard(webhookUrl, 'URL do webhook')}
                    className="p-2 text-gray-400 hover:text-primary transition-colors" title="Copiar URL">
                    <Copy size={16} />
                  </button>
                </div>
                <button type="button" onClick={handleRotateToken} disabled={rotating}
                  className="w-full py-2 border-2 border-dashed border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 rounded-xl hover:border-primary hover:text-primary transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                  {rotating ? <Loader2 size={16} className="animate-spin" /> : <Key size={16} />}
                  {status?.webhook_configurado ? 'Rotacionar token de webhook' : 'Gerar token de webhook'}
                  {isSandbox ? ' (sandbox)' : null}
                </button>

                {novoToken ? (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-3 space-y-2">
                    <p className="text-xs text-yellow-800 dark:text-yellow-300 font-medium">
                      Copie agora — este valor não será mostrado novamente. Cole em "Token de acesso" ao cadastrar o
                      webhook no painel do Asaas de {isSandbox ? 'sandbox' : 'produção'} da {empresa.nome}.
                    </p>
                    <div className="flex items-center gap-2">
                      <input readOnly value={novoToken}
                        className="flex-1 px-3 py-2 border border-yellow-300 dark:border-yellow-700 bg-white dark:bg-gray-800 dark:text-gray-200 rounded-lg text-xs font-mono" />
                      <button type="button" onClick={() => copyToClipboard(novoToken, 'Token')}
                        className="p-2 text-yellow-700 hover:text-yellow-900 transition-colors" title="Copiar token">
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

// ── Card de empresa ──────────────────────────────────────────

const EmpresaCard = ({
  empresa,
  onEdit,
  onRefresh,
}: {
  empresa: EmpresaParceira;
  onEdit: (e: EmpresaParceira) => void;
  onRefresh: () => void;
}) => {
  const { addToast } = useToast();
  const { profile } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [users, setUsers] = useState<Profile[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showAsaasCred, setShowAsaasCred] = useState(false);
  const [regerando, setRegerando] = useState<{ feitos: number; total: number } | null>(null);
  const [confirmRegerar, setConfirmRegerar] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean; title: string; description: string;
    onConfirm: () => void; isLoading: boolean;
  }>({ isOpen: false, title: '', description: '', onConfirm: () => {}, isLoading: false });

  const statusCfg = STATUS_CONFIG[empresa.status];

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email_corporativo, role, status, job_title, avatar_url, created_at')
        .eq('empresa_parceira_id', empresa.id)
        .order('full_name');
      if (error) throw error;
      setUsers((data || []) as Profile[]);
    } catch (err: any) {
      addToast('Erro ao carregar usuários.', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleExpand = () => {
    if (!expanded) loadUsers();
    setExpanded(e => !e);
  };

  const handleDeactivateUser = (u: Profile) => {
    setConfirmModal({
      isOpen: true,
      title: 'Desativar Usuário',
      description: `Desativar ${u.full_name}? O acesso será bloqueado imediatamente.`,
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(c => ({ ...c, isLoading: true }));
        try {
          await supabase.from('profiles').update({ status: 'inactive' }).eq('id', u.id);
          addToast('Usuário desativado.', 'success');
          logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'empresas_parceiras', acao: 'desativar_usuario_parceiro', origem: 'acao' });
          loadUsers();
        } catch {
          addToast('Erro ao desativar usuário.', 'error');
        } finally {
          setConfirmModal(c => ({ ...c, isOpen: false, isLoading: false }));
        }
      },
    });
  };

  const handleReactivateUser = async (u: Profile) => {
    try {
      await supabase.from('profiles').update({ status: 'active' }).eq('id', u.id);
      addToast('Usuário reativado.', 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'empresas_parceiras', acao: 'reativar_usuario_parceiro', origem: 'acao' });
      loadUsers();
    } catch {
      addToast('Erro ao reativar usuário.', 'error');
    }
  };

  /**
   * Regera em lote os PDFs dos boletos da parceira — usado quando os dados do emissor
   * ou o layout mudam e os boletos já emitidos ficaram com a versão velha.
   *
   * Roda no NAVEGADOR de propósito: gerar-boleto-gran exige JWT de usuário real
   * (auth.getUser + checagem de permissão no perfil), então não dá para disparar do
   * servidor nem com a service key. Aqui a sessão do adm já está na mão.
   *
   * Sequencial, não Promise.all: cada chamada monta o PDF, mescla a fatura da
   * distribuidora e sobe no Storage. Vinte em paralelo estouram o limite e deixam
   * metade pela metade sem dizer quais.
   */
  const handleRegerarPdfs = async () => {
    setConfirmRegerar(false);
    try {
      // PAGINAÇÃO OBRIGATÓRIA: o PostgREST corta em 1.000 linhas sem erro. Numa
      // parceira com mais boletos que isso, a tela regeraria só os 1.000 primeiros
      // e anunciaria "1.000 PDF(s) regerado(s)" — o operador acreditaria ter
      // terminado. É o mesmo cuidado do comentário acima sobre não falhar pela
      // metade em silêncio, que o truncamento derrubava.
      const PAGINA = 1000;
      const ids: number[] = [];
      let offset = 0;
      for (;;) {
        const { data, error } = await supabase
          .from('faturas')
          .select('id')
          .eq('empresa_parceira_id', empresa.id)
          .not('pdf_boleto_combinado', 'is', null)
          .not('num_cobranca_boleto', 'is', null)
          .in('status_fatura', ['Com boleto emitido / A enviar', 'Cobrança enviada'])
          .order('id')
          .range(offset, offset + PAGINA - 1);
        if (error) throw error;
        ids.push(...(data || []).map(f => f.id));
        if (!data || data.length < PAGINA) break;
        offset += PAGINA;
      }
      if (ids.length === 0) { addToast('Nenhum boleto com PDF para regerar.', 'info'); return; }

      setRegerando({ feitos: 0, total: ids.length });
      const falhas: number[] = [];
      for (let i = 0; i < ids.length; i++) {
        try { await gerarBoletoGran(ids[i]); }
        catch (err) { console.error(`Falha ao regerar PDF da fatura ${ids[i]}:`, err); falhas.push(ids[i]); }
        setRegerando({ feitos: i + 1, total: ids.length });
      }

      if (falhas.length === 0) {
        addToast(`${ids.length} PDF(s) regerado(s).`, 'success');
      } else {
        // Falha parcial nomeia as faturas: sem isso ninguém sabe quais refazer.
        addToast(`${ids.length - falhas.length} de ${ids.length} regerados. Falharam: ${falhas.join(', ')}`, 'warning');
      }
    } catch (err: any) {
      addToast('Erro ao regerar PDFs: ' + (err.message || 'Erro desconhecido'), 'error');
    } finally {
      setRegerando(null);
    }
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        {/* Header do card */}
        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Building2 size={20} className="text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-gray-900 dark:text-white">{empresa.nome}</h3>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusCfg.color}`}>
                    {statusCfg.label}
                  </span>
                </div>
                {empresa.cnpj && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{empresa.cnpj}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {empresa.banco_conta === 'asaas' && (
                <button
                  onClick={() => setShowAsaasCred(true)}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary dark:text-gray-400 dark:hover:text-primary transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <Key size={14} />
                  Credenciais Asaas
                </button>
              )}
              {empresa.banco_conta === 'asaas' && (
                <button
                  onClick={() => setConfirmRegerar(true)}
                  disabled={!!regerando}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary dark:text-gray-400 dark:hover:text-primary transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  title="Reescreve o PDF de cada boleto com os dados atuais do emissor. Não mexe na cobrança nem no código de barras."
                >
                  {regerando ? <Loader2 size={14} className="animate-spin" /> : <FileStack size={14} />}
                  {regerando ? `Regerando ${regerando.feitos}/${regerando.total}...` : 'Regerar PDFs'}
                </button>
              )}
              <button
                onClick={() => onEdit(empresa)}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary dark:text-gray-400 dark:hover:text-primary transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Pencil size={14} />
                Editar
              </button>
            </div>
          </div>

          {/* Métricas */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Divisão Vigor × {empresa.nome}</p>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Por UC</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Banco</p>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{BANCO_LABEL[empresa.banco_conta]}</p>
            </div>
          </div>

          {/* Módulos */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {empresa.modulos_habilitados.map(m => {
              const mod = MODULOS_DISPONIVEIS.find(x => x.key === m);
              return (
                <span key={m} className="text-xs bg-primary/10 text-primary dark:bg-primary/20 px-2 py-0.5 rounded-full font-medium">
                  {mod?.label || m}
                </span>
              );
            })}
          </div>
        </div>

        {/* Seção de usuários (expansível) */}
        <div className="border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={toggleExpand}
            className="w-full flex items-center justify-between px-5 py-3 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <span className="flex items-center gap-2 font-medium">
              <Users size={15} />
              Usuários de Acesso
            </span>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {expanded && (
            <div className="px-5 pb-5">
              {loadingUsers ? (
                <div className="flex justify-center py-4"><LoadingSpinner /></div>
              ) : (
                <div className="space-y-2">
                  {users.length === 0 ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-3">
                      Nenhum usuário cadastrado ainda.
                    </p>
                  ) : (
                    users.map(u => (
                      <div key={u.id} className="flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl px-3 py-2.5">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <img
                            src={u.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.full_name)}&background=random`}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover shrink-0"
                            onError={e => { e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(u.full_name)}&background=random` }}
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{u.full_name}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{u.email_corporativo || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full font-medium">
                            {ROLE_LABEL[u.role] || u.role}
                          </span>
                          {u.status === 'active' ? (
                            <button
                              onClick={() => handleDeactivateUser(u)}
                              title="Desativar"
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <Ban size={14} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivateUser(u)}
                              title="Reativar"
                              className="p-1 text-gray-400 hover:text-green-500 transition-colors"
                            >
                              <CheckCircle2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}

                  <button
                    onClick={() => setShowAddUser(true)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-200 dark:border-gray-600 text-sm text-gray-500 dark:text-gray-400 rounded-xl hover:border-primary hover:text-primary dark:hover:border-primary dark:hover:text-primary transition-all mt-1"
                  >
                    <UserPlus size={15} />
                    Adicionar Usuário
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showAddUser && (
        <AddUserModal
          empresa={empresa}
          onClose={() => setShowAddUser(false)}
          onSaved={() => { loadUsers(); onRefresh(); }}
        />
      )}

      {showAsaasCred && (
        <AsaasCredenciaisModal
          empresa={empresa}
          onClose={() => setShowAsaasCred(false)}
        />
      )}

      <ConfirmationModal
        isOpen={confirmRegerar}
        title="Regerar os PDFs dos boletos?"
        description={`Reescreve o PDF de cada boleto emitido da ${empresa.nome} com os dados atuais do emissor. A cobrança, o valor e o código de barras NÃO mudam — quem já recebeu o boleto continua podendo pagar normalmente. Leva alguns segundos por boleto.`}
        onConfirm={handleRegerarPdfs}
        onClose={() => setConfirmRegerar(false)}
        variant="info"
        confirmText="Regerar"
      />

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        description={confirmModal.description}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal(c => ({ ...c, isOpen: false }))}
        isLoading={confirmModal.isLoading}
        variant="danger"
        confirmText="Desativar"
      />
    </>
  );
};

// ── View principal ───────────────────────────────────────────

const EmpresasParceirasView: React.FC = () => {
  const { addToast } = useToast();
  const [empresas, setEmpresas] = useState<EmpresaParceira[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [editEmpresa, setEditEmpresa] = useState<EmpresaParceira | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('empresas_parceiras')
        .select('*')
        .order('nome');
      if (error) throw error;
      setEmpresas((data || []) as EmpresaParceira[]);
    } catch (err: any) {
      addToast('Erro ao carregar empresas parceiras.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold font-poppins text-gray-900 dark:text-white flex items-center gap-2">
            <Building2 size={24} className="text-primary" />
            Empresas Parceiras
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Gerencie contratos, percentuais e acessos das empresas que trazem clientes para a Vigor Energy.
          </p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
        >
          <Plus size={18} />
          Nova Empresa
        </button>
      </div>

      {/* Aviso sobre Inter */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-2xl p-4 flex items-start gap-3">
        <AlertCircle size={18} className="text-blue-500 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <span className="font-semibold">Credenciais bancárias (Banco Inter):</span> a configuração dos certificados mTLS e chaves de API do Inter será feita em <span className="font-semibold">APIs e Webhooks</span> após os certificados serem recebidos.
        </div>
      </div>

      {/* Conteúdo */}
      {loading ? (
        <div className="flex justify-center py-16"><LoadingSpinner /></div>
      ) : empresas.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Building2 size={48} className="text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-bold text-gray-500 dark:text-gray-400">Nenhuma empresa parceira cadastrada</h3>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Clique em "Nova Empresa" para começar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {empresas.map(ep => (
            <EmpresaCard
              key={ep.id}
              empresa={ep}
              onEdit={e => setEditEmpresa(e)}
              onRefresh={load}
            />
          ))}
        </div>
      )}

      {/* Modal: nova empresa */}
      {showNewModal && (
        <EmpresaModal
          empresa={null}
          onClose={() => setShowNewModal(false)}
          onSaved={load}
        />
      )}

      {/* Modal: editar empresa */}
      {editEmpresa && (
        <EmpresaModal
          empresa={editEmpresa}
          onClose={() => setEditEmpresa(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default EmpresasParceirasView;
